import { supabase } from '../lib/supabase';

const blogService = {
  // Get all articles (admin only)
  async getAllArticles(userId) {
    const { data, error } = await supabase?.from('blog_articles')?.select(`
        *,
        user_profiles (full_name)
      `)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(article => ({
      id: article?.id,
      authorId: article?.author_id,
      authorName: article?.user_profiles?.full_name || 'Rovyn Group',
      title: article?.title,
      slug: article?.slug,
      excerpt: article?.excerpt,
      content: article?.content,
      featuredImageUrl: article?.featured_image_url,
      videoEmbedUrl: article?.video_embed_url,
      topic: article?.topic,
      status: article?.status,
      readingTimeMinutes: article?.reading_time_minutes,
      viewCount: article?.view_count || 0,
      publishedAt: article?.published_at,
      createdAt: article?.created_at,
      updatedAt: article?.updated_at,
      comingSoon: article?.coming_soon || false,
      featured: article?.featured || false
    }));
  },

  // Get published articles with optional topic filter
  async getPublishedArticles(topic = null, sortBy = 'newest') {
    let query = supabase?.from('blog_articles')?.select(`
        *,
        user_profiles (full_name)
      `)?.eq('status', 'published');

    if (topic && topic !== 'all') {
      query = query?.eq('topic', topic);
    }

    if (sortBy === 'newest') {
      query = query?.order('published_at', { ascending: false });
    } else if (sortBy === 'oldest') {
      query = query?.order('published_at', { ascending: true });
    } else if (sortBy === 'popular') {
      query = query?.order('view_count', { ascending: false });
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(article => ({
      id: article?.id,
      authorId: article?.author_id,
      authorName: article?.user_profiles?.full_name || 'Rovyn Group',
      title: article?.title,
      slug: article?.slug,
      excerpt: article?.excerpt,
      content: article?.content,
      featuredImageUrl: article?.featured_image_url,
      videoEmbedUrl: article?.video_embed_url,
      topic: article?.topic,
      status: article?.status,
      readingTimeMinutes: article?.reading_time_minutes,
      viewCount: article?.view_count || 0,
      publishedAt: article?.published_at,
      createdAt: article?.created_at,
      updatedAt: article?.updated_at,
      comingSoon: article?.coming_soon || false,
      featured: article?.featured || false
    }));
  },

  // Get single article by slug
  async getArticleBySlug(slug) {
    const { data, error } = await supabase?.from('blog_articles')?.select(`
        *,
        user_profiles (full_name)
      `)?.eq('slug', slug)?.eq('status', 'published')?.single();

    if (error) throw error;

    return {
      id: data?.id,
      authorId: data?.author_id,
      authorName: data?.user_profiles?.full_name || 'Rovyn Group',
      title: data?.title,
      slug: data?.slug,
      excerpt: data?.excerpt,
      content: data?.content,
      featuredImageUrl: data?.featured_image_url,
      videoEmbedUrl: data?.video_embed_url,
      topic: data?.topic,
      status: data?.status,
      readingTimeMinutes: data?.reading_time_minutes,
      viewCount: data?.view_count || 0,
      publishedAt: data?.published_at,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
      comingSoon: data?.coming_soon || false,
      featured: data?.featured || false
    };
  },

  // Get article by ID (admin only)
  async getArticleById(articleId) {
    const { data, error } = await supabase?.from('blog_articles')?.select(`
        *,
        user_profiles (full_name)
      `)?.eq('id', articleId)?.single();

    if (error) throw error;

    return {
      id: data?.id,
      authorId: data?.author_id,
      authorName: data?.user_profiles?.full_name || 'Rovyn Group',
      title: data?.title,
      slug: data?.slug,
      excerpt: data?.excerpt,
      content: data?.content,
      featuredImageUrl: data?.featured_image_url,
      videoEmbedUrl: data?.video_embed_url,
      topic: data?.topic,
      status: data?.status,
      readingTimeMinutes: data?.reading_time_minutes,
      viewCount: data?.view_count || 0,
      publishedAt: data?.published_at,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
      comingSoon: data?.coming_soon || false,
      featured: data?.featured || false
    };
  },

  // Create new article
  async createArticle(articleData) {
    const { data, error } = await supabase?.from('blog_articles')?.insert([{
      author_id: articleData?.authorId,
      title: articleData?.title,
      slug: articleData?.slug,
      excerpt: articleData?.excerpt,
      content: articleData?.content,
      featured_image_url: articleData?.featuredImageUrl,
      video_embed_url: articleData?.videoEmbedUrl,
      topic: articleData?.topic,
      status: articleData?.status || 'draft',
      reading_time_minutes: articleData?.readingTimeMinutes,
      published_at: articleData?.status === 'published' ? new Date()?.toISOString() : null,
      coming_soon: articleData?.comingSoon || false,
      featured: articleData?.featured || false
    }])?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Update article
  async updateArticle(articleId, articleData) {
    const updateData = {
      title: articleData?.title,
      slug: articleData?.slug,
      excerpt: articleData?.excerpt,
      content: articleData?.content,
      featured_image_url: articleData?.featuredImageUrl,
      video_embed_url: articleData?.videoEmbedUrl,
      topic: articleData?.topic,
      status: articleData?.status,
      reading_time_minutes: articleData?.readingTimeMinutes,
      coming_soon: articleData?.comingSoon || false,
      featured: articleData?.featured || false
    };

    // Set published_at when publishing for the first time
    if (articleData?.status === 'published') {
      const { data: currentArticle } = await supabase?.from('blog_articles')?.select('published_at')?.eq('id', articleId)?.single();
      if (!currentArticle?.published_at) {
        updateData.published_at = new Date()?.toISOString();
      }
    }

    const { data, error } = await supabase?.from('blog_articles')?.update(updateData)?.eq('id', articleId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Delete article
  async deleteArticle(articleId) {
    const { error } = await supabase?.from('blog_articles')?.delete()?.eq('id', articleId);
    if (error) throw error;
  },

  // Increment view count
  async incrementViewCount(articleId) {
    const { error } = await supabase?.rpc('increment_article_views', { article_id: articleId });
    if (error) console.error('Failed to increment view count:', error);
  },

  // Upload image to storage
  async uploadImage(file, path) {
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
    const filePath = path ? `${path}/${fileName}` : fileName;

    const { data, error } = await supabase?.storage?.from('blog-images')?.upload(filePath, file);

    if (error) throw error;

    const { data: { publicUrl } } = supabase?.storage?.from('blog-images')?.getPublicUrl(filePath);

    return publicUrl;
  },

  // Delete image from storage
  async deleteImage(filePath) {
    const { error } = await supabase?.storage?.from('blog-images')?.remove([filePath]);
    if (error) throw error;
  },

  // Get related articles
  async getRelatedArticles(currentArticleId, topic, limit = 3) {
    const { data, error } = await supabase?.from('blog_articles')?.select(`
        *,
        user_profiles (full_name)
      `)?.eq('status', 'published')?.eq('topic', topic)?.neq('id', currentArticleId)?.order('published_at', { ascending: false })?.limit(limit);

    if (error) throw error;

    return data?.map(article => ({
      id: article?.id,
      authorId: article?.author_id,
      authorName: article?.user_profiles?.full_name || 'Rovyn Group',
      title: article?.title,
      slug: article?.slug,
      excerpt: article?.excerpt,
      featuredImageUrl: article?.featured_image_url,
      topic: article?.topic,
      readingTimeMinutes: article?.reading_time_minutes,
      publishedAt: article?.published_at,
      comingSoon: article?.coming_soon || false,
      featured: article?.featured || false
    }));
  },

  // Get article tags
  async getArticleTags(articleId) {
    const { data, error } = await supabase?.from('article_tags')?.select('tag_name')?.eq('article_id', articleId);

    if (error) throw error;

    return data?.map(tag => tag?.tag_name) || [];
  },

  // Save article tags
  async saveArticleTags(articleId, tags) {
    // First, delete existing tags
    await supabase?.from('article_tags')?.delete()?.eq('article_id', articleId);

    // Then insert new tags
    if (tags?.length > 0) {
      const tagData = tags?.map(tagName => ({
        article_id: articleId,
        tag_name: tagName
      }));

      const { error } = await supabase?.from('article_tags')?.insert(tagData);
      if (error) throw error;
    }
  }
};

export default blogService;